import { useCallback, useEffect, useRef } from 'react';
import { getPreviousLocation } from 'helpers/navigationHistory';

import { sessionId } from '@atlas/core';

import { POSTHOG_CTA, POSTHOG_EVENTS } from 'common/constants';

const useRegistrationPostHog = ({ activeStep, cxFromParam, cxFromPath, postHog, stepsAmount }) => {
  const hasTransitionedRef = useRef(false);

  const pageRefer = cxFromParam ?? cxFromPath ?? getPreviousLocation()?.pathname ?? window.location.pathname;

  const concatStep = useCallback(
    step => {
      const concatData = {
        cta_clicked: null,
        email: null,
        leaving_cta_clicked: null,
        leaving_pop_up: null,
        manual_closed_timestamp: null,
        page_refer: pageRefer,
        registration_step: step,
        session_id: sessionId,
        shown_timestamp: Date.now(),
        total_registration_steps: stepsAmount,
      };
      postHog.concat(POSTHOG_EVENTS.REGISTRATION, concatData);
    },
    [postHog, pageRefer, stepsAmount],
  );

  useEffect(() => {
    if (!stepsAmount || hasTransitionedRef.current) return;
    concatStep(activeStep + 1);
  }, [stepsAmount]);

  const handleNextStep = useCallback(
    (ctaClicked, skipConcat = false, email = null) => {
      hasTransitionedRef.current = true;
      if (ctaClicked) {
        postHog.send(POSTHOG_EVENTS.REGISTRATION, { cta_clicked: ctaClicked, email });
      }
      if (!skipConcat) {
        concatStep(activeStep + 2);
      }
    },
    [postHog, concatStep, activeStep],
  );

  const handleLeave = useCallback(() => {
    postHog.send(POSTHOG_EVENTS.REGISTRATION, { manual_closed_timestamp: Date.now() });
  }, [postHog]);

  const handleBackClick = useCallback(() => {
    postHog.send(POSTHOG_EVENTS.REGISTRATION, { cta_clicked: POSTHOG_CTA.BACK_TO_PREV_STEP });
    concatStep(activeStep);
  }, [postHog, concatStep, activeStep]);

  const handleRegistrationSuccess = useCallback(
    ({ clientId, email }) => {
      postHog.send(POSTHOG_EVENTS.REGISTRATION, {
        client_id: clientId,
        cta_clicked: POSTHOG_CTA.NEXT_STEP,
        email: email ?? null,
      });
    },
    [postHog],
  );

  return {
    handleBackClick,
    handleLeave,
    handleNextStep,
    handleRegistrationSuccess,
  };
};

export default useRegistrationPostHog;
